import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Container, Paper } from "@mui/material";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import { useHistory } from "react-router-dom";

// ── Terminal lines that type out one by one ──
const terminalLines = [
  {
    text: "$ cloudtorrent --fetch-page /unknown-path",
    color: "#90caf9",
    delay: 0,
  },
  { text: "  Connecting to cloud server...", color: "#b0bec5", delay: 600 },
  { text: "  Resolving DNS...", color: "#b0bec5", delay: 1200 },
  { text: "  Fetching page metadata...", color: "#b0bec5", delay: 1800 },
  { text: "", delay: 2400 },
  { text: "  [ERROR] Response: 404 Not Found", color: "#ef9a9a", delay: 2600 },
  {
    text: "  [ERROR] Page does not exist on this server.",
    color: "#ef9a9a",
    delay: 3000,
  },
  {
    text: "  [ERROR] No seeders found for this route.",
    color: "#ef9a9a",
    delay: 3400,
  },
  { text: "", delay: 3800 },
  { text: "  Traceback:", color: "#ffe082", delay: 4000 },
  {
    text: "    → Router failed to match path '/unknown-path'",
    color: "#ffe082",
    delay: 4300,
  },
  { text: "    → Fallback handler triggered", color: "#ffe082", delay: 4600 },
  { text: "    → Rendering 404 component", color: "#ffe082", delay: 4900 },
  { text: "", delay: 5200 },
  {
    text: "  Suggestion: Navigate to / or /search",
    color: "#a5d6a7",
    delay: 5400,
  },
  { text: "$ _", color: "#90caf9", delay: 5800 },
];

const Error404 = () => {
  const history = useHistory();
  const [visibleLines, setVisibleLines] = useState([]);
  const [blink, setBlink] = useState(true);

  // Reveal lines one by one based on delay
  useEffect(() => {
    terminalLines.forEach((line, i) => {
      setTimeout(() => {
        setVisibleLines((prev) => [...prev, i]);
      }, line.delay);
    });
  }, []);

  // Blinking cursor
  useEffect(() => {
    const t = setInterval(() => setBlink((b) => !b), 530);
    return () => clearInterval(t);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        {/* ── Icon + Heading ── */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "primary.main",
              borderRadius: "50%",
              width: 90,
              height: 90,
              mb: 2.5,
              animation: "glow 2s ease-in-out infinite",
              "@keyframes glow": {
                "0%": { boxShadow: "0 0 20px rgba(25,118,210,0.3)" },
                "50%": { boxShadow: "0 0 55px rgba(25,118,210,0.75)" },
                "100%": { boxShadow: "0 0 20px rgba(25,118,210,0.3)" },
              },
            }}
          >
            <CloudOffIcon sx={{ fontSize: 46, color: "#fff" }} />
          </Box>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "5.5rem", md: "8rem" },
              fontWeight: 900,
              lineHeight: 1,
              background: "linear-gradient(135deg, #1976d2 20%, #42a5f5 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-3px",
              mb: 1,
            }}
          >
            404
          </Typography>

          <Typography
            variant="h6"
            fontWeight={700}
            color="text.primary"
            mb={0.5}
          >
            Page Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This route doesn't exist in the CloudTorrent universe.
          </Typography>
        </Box>

        {/* ── Terminal Mockup ── */}
        <Paper
          elevation={6}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            mb: 4,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* Terminal title bar */}
          <Box
            sx={{
              bgcolor: "#1a1a2e",
              px: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {/* Traffic light dots */}
            {["#ff5f57", "#febc2e", "#28c840"].map((color, i) => (
              <Box
                key={i}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: color,
                }}
              />
            ))}
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontFamily: "monospace",
                ml: 1,
              }}
            >
              cloudtorrent — bash — 80×24
            </Typography>
          </Box>

          {/* Terminal body */}
          <Box
            sx={{
              bgcolor: "#0d1117",
              px: 3,
              py: 2.5,
              minHeight: 320,
              fontFamily: "monospace",
              fontSize: "0.8rem",
              lineHeight: 1.9,
            }}
          >
            {terminalLines.map((line, i) => (
              <Box
                key={i}
                sx={{
                  opacity: visibleLines.includes(i) ? 1 : 0,
                  transform: visibleLines.includes(i)
                    ? "translateY(0)"
                    : "translateY(4px)",
                  transition: "opacity 0.3s ease, transform 0.3s ease",
                  minHeight: "1.4em",
                }}
              >
                {line.text === "$ _" ? (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      color: "#90caf9",
                    }}
                  >
                    ${" "}
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 8,
                        height: "1em",
                        bgcolor: blink ? "#90caf9" : "transparent",
                        verticalAlign: "middle",
                        transition: "background 0.1s",
                      }}
                    />
                  </Typography>
                ) : (
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      color: line.color || "#e0e0e0",
                      whiteSpace: "pre",
                    }}
                  >
                    {line.text}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>

        {/* ── Buttons ── */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => history.push("/")}
            sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            Go Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Error404;
